var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css, } from '@emotion/react';
import { createRef, useMemo, } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { useKeyboardFocusState } from '../../utils/useKeyboardFocusState';
import { FadeReveal } from '../FadeReveal/index';
import { borderWidth, counterStyles, errorMessageStyles, errorStyles, inputHeights, inputStyles as baseInputStyles, placeholderContainerStyles as basePlaceholderContainerStyles, useErrorSpacingStyles, useInputContainerStyles, useLabelStyles, usePlaceholderContentStyles, } from '../TextInput/styles';
const scrollbarOffset = 17;
const inputValueVerticalSpacingStyles = {
    medium: css `
    padding-bottom: ${theme.spacing.base};
    padding-top: calc(${theme.spacing.x4} + ${theme.spacing.base});
  `,
    small: css `
    padding-bottom: calc(${theme.spacing.base} / 2);
    padding-top: calc(${theme.spacing.x4} - (${theme.spacing.base} / 2));
  `,
};
const placeholderContainerStyles = css `
  ${basePlaceholderContainerStyles}
  top: 0;
`;
const defaultProps = {
    borderless: false,
    disabled: false,
    error: '',
    hasErrorSpacing: false,
    id: '',
    inputRef: createRef(),
    label: '',
    maxRows: undefined,
    minHeight: undefined,
    minRows: 1,
    onChange: () => { },
    placeholder: '',
    required: false,
    resizable: true,
    size: 'medium',
    touched: false,
    value: '',
};
const Textarea = (_a) => {
    var { borderless = defaultProps.borderless, disabled = defaultProps.disabled, error = defaultProps.error, hasErrorSpacing = defaultProps.hasErrorSpacing, id = defaultProps.id, inputRef = defaultProps.inputRef, label = defaultProps.label, maxLength, 
    /* eslint-disable-next-line deprecation/deprecation */
    minHeight = defaultProps.minHeight, name, placeholder = defaultProps.placeholder, required = defaultProps.required, resizable = defaultProps.resizable, size = 'medium', touched = defaultProps.touched, value = defaultProps.value } = _a, rest = __rest(_a, ["borderless", "disabled", "error", "hasErrorSpacing", "id", "inputRef", "label", "maxLength", "minHeight", "name", "placeholder", "required", "resizable", "size", "touched", "value"]);
    const focusInput = () => {
        var _a;
        (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const isKeyboardFocused = useKeyboardFocusState(inputRef);
    const placeholderVariationStyles = useMemo(() => (css `
        ${inputValueVerticalSpacingStyles[size]}
        align-self: flex-start;
      `), [
        size,
    ]);
    const baseLabelStyles = useLabelStyles({
        isValueVisible: !!value,
        size,
    });
    const labelStyles = useMemo(() => (css `
        ${baseLabelStyles}
        background-clip: padding-box;
        background-color: ${theme.colors.gray100};
        border-bottom-width: 0;
        border-color: transparent;
        border-left-width: ${styleUtils.rem(borderWidth)};
        border-right-width: ${styleUtils.rem(borderWidth)};
        border-style: solid;
        border-top-width: ${styleUtils.rem(borderWidth)};
        padding-right: 0!important;
        pointer-events: none;
        width: calc(100% - ${styleUtils.rem(scrollbarOffset)});

        ${disabled && css `
          background-color: ${theme.colors.gray200};
          color: ${theme.colors.gray500};
        `}
      `), [
        baseLabelStyles,
        disabled,
    ]);
    const basePlaceholderContentStyles = usePlaceholderContentStyles({
        isLabelVisible: !!label,
        isValueVisible: !!value,
        size,
    });
    const placeholderContentStyles = useMemo(() => (css `
        ${basePlaceholderContentStyles}
        align-self: flex-start;
        border-left: ${styleUtils.rem(borderWidth)} solid transparent;
        ${label && css `
          ${placeholderVariationStyles}
        `}
      `), [
        basePlaceholderContentStyles,
        label,
        placeholderVariationStyles,
    ]);
    const textAreaContainerStyles = useInputContainerStyles({
        isDisabled: disabled,
        isFocused: isKeyboardFocused,
        isLabelVisible: !!label,
    });
    const textAreaStyles = useMemo(() => (css `
        ${styleUtils.resetAll}
        ${styleUtils.borderBox}
        ${baseInputStyles}
        background-color: ${theme.colors.gray100};
        border-color: ${theme.colors.gray400};
        border-style: solid;
        border-width: ${styleUtils.rem(borderWidth)};
        padding-bottom: ${theme.spacing.x3};
        padding-top: ${theme.spacing.x3};
        min-height: ${styleUtils.rem(inputHeights[size])};
        resize: vertical;

        &:focus {
          border-color: ${theme.colors.gray700};

          ${borderless && css `
            border: none;
          `}
        }

        ${borderless && css `
          border: none;
        `}
        ${label && placeholderVariationStyles}
        ${error && touched && errorStyles}
        ${!resizable && css `
          resize: none;
        `}
        ${minHeight && css `
          min-height: ${styleUtils.rem(minHeight)};
        `}
      `), [
        borderless,
        error,
        label,
        minHeight,
        placeholderVariationStyles,
        resizable,
        size,
        touched,
    ]);
    const errorSpacingStyles = useErrorSpacingStyles({
        isErrorSpacingVisible: hasErrorSpacing,
        size,
    });
    const wrapperStyles = useMemo(() => (css `
        ${hasErrorSpacing && errorSpacingStyles}
      `), [
        errorSpacingStyles,
        hasErrorSpacing,
    ]);
    return (_jsxs("div", Object.assign({ css: wrapperStyles }, { children: [_jsxs("div", Object.assign({ css: textAreaContainerStyles }, { children: [_jsx(TextareaAutosize, Object.assign({ css: textAreaStyles, disabled: disabled, id: id, maxLength: maxLength, name: name, ref: inputRef, required: required, value: value }, rest)), label && (_jsxs("label", Object.assign({ css: labelStyles, htmlFor: name, onClick: focusInput }, { children: [_jsx("span", { children: label }), maxLength
                                && value
                                && (_jsxs("span", Object.assign({ css: counterStyles }, { children: [maxLength - value.length, "/", maxLength] })))] }))), placeholder && (_jsx("div", Object.assign({ css: placeholderContainerStyles }, { children: _jsx("div", Object.assign({ css: placeholderContentStyles, onClick: disabled ? undefined : focusInput }, { children: placeholder })) })))] })), _jsx(FadeReveal, Object.assign({ in: !!error && !!touched, unmountOnExit: true }, { children: _jsx("span", Object.assign({ css: errorMessageStyles, role: "alert" }, { children: error })) }))] })));
};
export { Textarea };
